import React, { memo, useCallback, useState } from 'react';
import { cn } from '@/lib/utils';
import { 
  Search, 
  SlidersHorizontal,
  X,
  ChevronDown,
  MessageCircle,
  Instagram,
  Globe,
  Bot,
  User,
  Clock,
  AlertTriangle,
  CheckCircle,
  Zap,
  Archive,
  RefreshCw
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';

// ============================================================================
// FILTER DEFINITIONS
// ============================================================================

const STATUS_FILTERS = [
  { value: 'all', label: 'Semua Status', icon: null },
  { value: 'attention', label: 'Perlu Perhatian', icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> },
  { value: 'unread', label: 'Belum Dibaca', icon: <div className="w-2 h-2 rounded-full bg-indigo-500" /> },
  { value: 'open', label: 'Open', icon: <Clock className="w-3.5 h-3.5 text-green-500" /> },
  { value: 'pending', label: 'Pending', icon: <Clock className="w-3.5 h-3.5 text-amber-500" /> },
  { value: 'resolved', label: 'Resolved', icon: <CheckCircle className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" /> },
  { value: 'archived', label: 'Arsip', icon: <Archive className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" /> },
];

const CHANNEL_FILTERS = [
  { value: 'all', label: 'Semua Channel', icon: null },
  { value: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle className="w-3.5 h-3.5 text-green-500" /> },
  { value: 'messenger', label: 'Messenger', icon: <MessageCircle className="w-3.5 h-3.5 text-blue-500" /> },
  { value: 'instagram', label: 'Instagram', icon: <Instagram className="w-3.5 h-3.5 text-pink-500" /> },
  { value: 'website', label: 'Website', icon: <Globe className="w-3.5 h-3.5 text-amber-500" /> },
];

const HANDLER_FILTERS = [
  { value: 'all', label: 'Semua Agent', icon: null },
  { value: 'bot', label: 'Bot', icon: <Bot className="w-3.5 h-3.5 text-purple-500" /> },
  { value: 'manual', label: 'Manual', icon: <User className="w-3.5 h-3.5 text-blue-500" /> },
];

const PRIORITY_FILTERS = [
  { value: 'all', label: 'Semua Priority', icon: null },
  { value: 'urgent', label: 'Urgent', icon: <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> },
  { value: 'high', label: 'High', icon: <Zap className="w-3.5 h-3.5 text-orange-500" /> },
  { value: 'normal', label: 'Normal', icon: <div className="w-2 h-2 rounded-full bg-indigo-500" /> },
  { value: 'low', label: 'Low', icon: <div className="w-2 h-2 rounded-full bg-slate-400" /> },
];

// ============================================================================
// FILTER SELECT (inside popover)
// ============================================================================

const FilterSelect = memo(({ label, value, options, onChange }) => {
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  const isFiltered = value !== 'all';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-all border',
            isFiltered
              ? 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300'
              : 'bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-800 text-slate-600 dark:text-zinc-400 hover:border-slate-300'
          )}
        >
          <span className="text-xs text-slate-500 font-medium dark:text-zinc-400">{label}</span>
          <div className="flex items-center gap-1.5">
            {selectedOption.icon}
            <span className="text-xs font-medium">{selectedOption.label}</span>
            <ChevronDown className="w-3 h-3 opacity-50" />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center gap-2 cursor-pointer',
              value === option.value && 'bg-indigo-50 dark:bg-indigo-950/30'
            )}
          >
            {option.icon}
            <span className="flex-1">{option.label}</span>
            {value === option.value && (
              <CheckCircle className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

FilterSelect.displayName = 'FilterSelect';

// ============================================================================
// ACTIVE FILTER BADGE
// ============================================================================

const ActiveFilterBadge = memo(({ label, onRemove }) => (
  <Badge 
    variant="secondary" 
    className="flex items-center gap-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
  >
    <span className="text-xs">{label}</span>
    <button 
      onClick={onRemove}
      className="ml-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
    >
      <X className="w-3 h-3" />
    </button>
  </Badge>
));

ActiveFilterBadge.displayName = 'ActiveFilterBadge';

// ============================================================================
// MAIN INBOX FILTERS COMPONENT
// ============================================================================

export const InboxFilters = memo(({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  channelFilter,
  onChannelChange,
  handlerFilter,
  onHandlerChange,
  priorityFilter,
  onPriorityChange,
  totalCount = 0,
  unreadCount = 0,
  onClearFilters,
  botToggle,
  className = ''
}) => {
  const [filterOpen, setFilterOpen] = useState(false);

  const activeFiltersCount = [
    statusFilter !== 'all',
    channelFilter !== 'all',
    handlerFilter !== 'all',
    priorityFilter !== 'all',
  ].filter(Boolean).length;

  const handleClearAll = useCallback(() => {
    onStatusChange?.('all');
    onChannelChange?.('all');
    onHandlerChange?.('all');
    onPriorityChange?.('all');
    onSearchChange?.('');
    onClearFilters?.();
  }, [onStatusChange, onChannelChange, onHandlerChange, onPriorityChange, onSearchChange, onClearFilters]);

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* Single row: Search + Filter button */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
          <Input
            type="text"
            placeholder="Cari nama, nomor, atau pesan..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-8 text-sm bg-white dark:bg-zinc-900"
            data-testid="inbox-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600 dark:text-zinc-400"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Popover */}
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-8 px-3 gap-1.5 flex-shrink-0',
                activeFiltersCount > 0 && 'border-blue-400 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300'
              )}
              data-testid="inbox-filter-btn"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span className="text-xs font-medium hidden sm:inline">Filter</span>
              {activeFiltersCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px] flex items-center justify-center font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-3 space-y-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-700 dark:text-zinc-200">Filter Percakapan</span>
              {activeFiltersCount > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium dark:text-indigo-400"
                >
                  Reset semua
                </button>
              )}
            </div>
            <FilterSelect label="Status" value={statusFilter} options={STATUS_FILTERS} onChange={onStatusChange} />
            <FilterSelect label="Channel" value={channelFilter} options={CHANNEL_FILTERS} onChange={onChannelChange} />
            <FilterSelect label="Agent" value={handlerFilter} options={HANDLER_FILTERS} onChange={onHandlerChange} />
            <FilterSelect label="Priority" value={priorityFilter} options={PRIORITY_FILTERS} onChange={onPriorityChange} />
          </PopoverContent>
        </Popover>

        {/* Bot/Manual Toggle — mobile only (beside filter button) */}
        {botToggle?.show && (
          <div className="sm:hidden flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-zinc-800 rounded-lg flex-shrink-0">
            <User className={`w-3.5 h-3.5 ${!botToggle.checked ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-zinc-400'} dark:text-indigo-400`} />
            <Switch
              checked={botToggle.checked}
              onCheckedChange={botToggle.onCheckedChange}
              disabled={botToggle.disabled}
              className="data-[state=checked]:bg-indigo-600 scale-75"
            />
            <Bot className={`w-3.5 h-3.5 ${botToggle.checked ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-zinc-400'} dark:text-indigo-400`} />
            {botToggle.loading && <RefreshCw className="w-3 h-3 animate-spin" />}
          </div>
        )}
      </div>

      {/* Active filter badges (only when filters applied) */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {statusFilter !== 'all' && (
            <ActiveFilterBadge label={STATUS_FILTERS.find(f => f.value === statusFilter)?.label} onRemove={() => onStatusChange('all')} />
          )}
          {channelFilter !== 'all' && (
            <ActiveFilterBadge label={CHANNEL_FILTERS.find(f => f.value === channelFilter)?.label} onRemove={() => onChannelChange('all')} />
          )}
          {handlerFilter !== 'all' && (
            <ActiveFilterBadge label={HANDLER_FILTERS.find(f => f.value === handlerFilter)?.label} onRemove={() => onHandlerChange('all')} />
          )}
          {priorityFilter !== 'all' && (
            <ActiveFilterBadge label={PRIORITY_FILTERS.find(f => f.value === priorityFilter)?.label} onRemove={() => onPriorityChange('all')} />
          )}
          <button onClick={handleClearAll} className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium ml-1 dark:text-indigo-400">
            Clear
          </button>
        </div>
      )}
    </div>
  );
});

InboxFilters.displayName = 'InboxFilters';

export default InboxFilters;
